"use client";

import { Heart, MessageCircle } from "lucide-react";
import type {
  UserReplyItem,
  UserReplyPerson,
  UserReplyQuote,
} from "@/actions/user-replies";
import {
  FeedCardAvatar,
  FeedCardIdentity,
} from "@/components/feed/feed-card-header";
import {
  AssistantAvatar,
  HiiveBotMark,
} from "@/components/mcp/assistant-avatar";
import { SongCard } from "@/components/music/song-card";
import { CommentVoiceNotePlayer } from "@/components/posts/comment-voice-note";
import { RichPostText } from "@/components/posts/rich-post-text";
import { AppImage } from "@/components/ui/app-image";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { StickerBlob } from "@/components/wallet/sticker-blob";
import { formatRelativeTimeCompact } from "@/lib/dates/relative-time";
import { normalizeMediaAsset } from "@/lib/media";
import { cn } from "@/lib/utils";
import { findSticker } from "@/lib/wallet/sticker-share";

/**
 * One reply on a profile's "Posts & replies" tab, laid out like X: what it
 * answers on top, one rail down the avatar column, then the reply itself. A
 * reply deeper in a thread stacks the conversation the way X's timeline
 * does: the post, "Show N more replies" for the comments in between, the
 * comment it answers, then the reply. The whole card opens the thread on this
 * reply, with every ancestor stacked above it.
 *
 * `showReplier` is the home feed's version: the card opens on someone else's
 * words, so a "Name replied" line on top says why it is in the feed, like X's
 * social context line.
 */
export function ProfileReplyRow({
  reply,
  showReplier = false,
  className,
}: {
  reply: UserReplyItem;
  showReplier?: boolean;
  className?: string;
}) {
  const hiddenCount = reply.thread?.hiddenCount ?? 0;

  return (
    <article
      className={cn(
        "relative px-3 pt-3 pb-3 transition-colors hover:bg-surface-alt/40",
        className,
      )}
    >
      {/* Stretched link: the card opens the thread, while the avatars, names
          and players drawn over it keep their own targets. */}
      <BackAwareLink
        href={reply.href}
        aria-label={`Open the thread for ${reply.author.displayName}'s reply`}
        className="absolute inset-0"
      />
      <div className="pointer-events-none relative [&_a]:pointer-events-auto [&_audio]:pointer-events-auto [&_button]:pointer-events-auto">
        {showReplier ? (
          <p className="mb-1.5 flex items-center gap-1.5 pl-[50px] text-xs text-text-tertiary">
            <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="truncate">
              <span className="font-medium text-text-secondary">
                {reply.author.displayName}
              </span>{" "}
              replied
            </span>
          </p>
        ) : null}

        {reply.thread ? (
          <>
            <QuoteRow quote={reply.thread.post} clamp="line-clamp-2" />
            {hiddenCount > 0 ? (
              // Dots continue the rail across the replies left out.
              <div className="flex items-center gap-2.5 pb-3">
                <span
                  aria-hidden="true"
                  className="flex w-10 shrink-0 flex-col items-center gap-1"
                >
                  <span className="size-0.5 rounded-full bg-text-tertiary" />
                  <span className="size-0.5 rounded-full bg-text-tertiary" />
                  <span className="size-0.5 rounded-full bg-text-tertiary" />
                </span>
                <span className="text-[13px] text-brand">
                  Show {hiddenCount} more{" "}
                  {hiddenCount === 1 ? "reply" : "replies"}
                </span>
              </div>
            ) : null}
          </>
        ) : null}

        {/* What this reply answers, with the rail into the reply's avatar. */}
        <QuoteRow quote={reply.parent} clamp="line-clamp-3" />

        {/* The reply. */}
        <div className="flex gap-2.5">
          <FeedCardAvatar
            displayName={reply.author.displayName}
            handle={reply.author.username}
            avatarUrl={reply.author.avatarUrl}
          />
          <div className="min-w-0 flex-1">
            <FeedCardIdentity
              displayName={reply.author.displayName}
              handle={reply.author.username}
              createdAt={new Date(reply.createdAt)}
              timeAgo={formatRelativeTimeCompact(new Date(reply.createdAt))}
            />
            <p className="text-xs text-text-tertiary">
              Replying to{" "}
              <span className="text-brand">{nameOf(reply.parent.author)}</span>
            </p>
            {reply.content ? (
              <RichPostText
                content={reply.content}
                mentions={reply.mentions}
                className="mt-1"
                textClassName="text-[15px] leading-snug text-text-primary"
              />
            ) : null}
            <ReplyAttachment reply={reply} />
            <div className="mt-2 flex items-center gap-5 text-xs text-text-tertiary tabular-nums">
              <span className="flex items-center gap-1.5">
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                {reply.replyCount > 0 ? reply.replyCount : null}
                <span className="sr-only">
                  {reply.replyCount === 1 ? "reply" : "replies"}
                </span>
              </span>
              <span className="flex items-center gap-1.5">
                <Heart className="h-4 w-4" aria-hidden="true" />
                {reply.likeCount > 0 ? reply.likeCount : null}
                <span className="sr-only">
                  {reply.likeCount === 1 ? "like" : "likes"}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function ReplyAttachment({ reply }: { reply: UserReplyItem }) {
  if (reply.voice) {
    return (
      <div className="mt-1.5">
        <CommentVoiceNotePlayer voice={reply.voice} />
      </div>
    );
  }
  if (reply.music) {
    return (
      <SongCard
        music={reply.music}
        surface="comment"
        interactive
        className="mt-1.5 max-w-[19rem]"
      />
    );
  }
  if (reply.media?.kind === "hiive_sticker") {
    const sticker = findSticker(reply.media.stickerId);
    if (!sticker) return null;
    // A still preview: this row is itself the link into the thread, where the
    // sticker is live and tappable.
    return (
      <div className="mt-1.5 flex items-center gap-2">
        <StickerBlob sticker={sticker} size={56} />
        <span className="text-[13px] font-semibold text-text-secondary">
          {sticker.title}
        </span>
      </div>
    );
  }
  const media = reply.media ? normalizeMediaAsset(reply.media.asset) : null;
  if (!reply.media || !media) return null;
  const poster = media.kind === "video" ? media.poster?.url : media.url;
  if (!poster) return null;
  const isSticker = reply.media.kind === "sticker";
  return (
    // biome-ignore lint/performance/noImgElement: GIF/sticker frames must keep animating; this is a small preview.
    <img
      src={poster}
      alt={reply.media.kind === "photo" ? "Reply photo" : "GIF"}
      loading="lazy"
      className={cn(
        "mt-1.5 block max-h-48 w-auto max-w-[min(16rem,100%)]",
        isSticker
          ? "object-contain"
          : "rounded-2xl border border-border object-cover",
      )}
    />
  );
}

/** One quoted step of the conversation, with the rail down to the next. */
function QuoteRow({
  quote,
  clamp,
}: {
  quote: UserReplyQuote;
  clamp: "line-clamp-2" | "line-clamp-3";
}) {
  return (
    <div className="relative flex gap-2.5 pb-3">
      <span
        aria-hidden="true"
        className="absolute top-11 bottom-0 left-5 w-px bg-border"
      />
      <PersonAvatar person={quote.author} />
      <div className="flex min-w-0 flex-1 gap-3">
        <div className="min-w-0 flex-1">
          <FeedCardIdentity
            displayName={quote.author.displayName}
            handle={quote.author.username}
            createdAt={new Date(quote.createdAt)}
            timeAgo={formatRelativeTimeCompact(new Date(quote.createdAt))}
            isBlocked={quote.author.isBlocked}
            hideHandle={quote.author.isHiiveBot}
            extraBadge={
              quote.author.isHiiveBot ? (
                <HiiveBotMark className="size-3.5" />
              ) : undefined
            }
          />
          {quote.text ? (
            <p
              className={cn(
                "mt-0.5 text-[14px] leading-5 whitespace-pre-wrap text-text-secondary",
                clamp,
              )}
            >
              {quote.text}
            </p>
          ) : null}
        </div>
        {quote.thumbnailUrl ? (
          <AppImage
            src={quote.thumbnailUrl}
            alt=""
            width={112}
            height={112}
            sizes="56px"
            className="mt-0.5 h-14 w-14 shrink-0 rounded-xl border border-border object-cover"
            imageContext="comment-media"
          />
        ) : null}
      </div>
    </div>
  );
}

/** A Hiive bot reply wears the bot's face, never the asker's. */
function PersonAvatar({ person }: { person: UserReplyPerson }) {
  if (person.isHiiveBot) return <AssistantAvatar />;
  return (
    <FeedCardAvatar
      displayName={person.displayName}
      handle={person.username}
      avatarUrl={person.avatarUrl}
      isBlocked={person.isBlocked}
    />
  );
}

const nameOf = (person: UserReplyPerson) =>
  person.username ? `@${person.username}` : person.displayName;
